/**
 * Fog of war. Two related predicates, deliberately different:
 *
 *  - visibleTo — the STRICT rule the AI reasons under (manual: game-rules). A side
 *    knows an enemy ship only via contact (shared tile), combat last round, or a
 *    friendly Carrier within 2 (surface only). This is the fog-honest choke point:
 *    the difficulty ladder scales the AI's awareness above this baseline
 *    (perceivedEnemies), so it must stay strict or the ladder loses its dial.
 *
 *  - visibleOnBoard — the more forgiving view the HUMAN sees rendered: any surface
 *    enemy sitting in an unfogged tile shows, so "clear water ⇒ visible ship". It
 *    shares tileInView with the shroud, so the two can never disagree.
 *
 * Submerged subs are hidden from both until they reveal themselves: once they've
 * fought (`Ship.fought`) or a ship shares their exact tile (contact). WP-3 extends
 * visibleTo with the fog-filtered view the AI is restricted to.
 */
import { hexDist } from './board';
import { serialize, type GameState, type Ship, type ShipType, type Side } from './state';

/** How many hexes of sea each ship keeps lit around itself for the shroud/board
 *  view. Carriers and patrol-boat scouts see farther, matching their spotting
 *  role. A rewrite invention (the manual has no per-ship sight radius); it drives
 *  both the fog shroud and visibleOnBoard so the two can never disagree. */
export const SHIP_SIGHT: Record<ShipType, number> = {
  CV: 3, PT: 3, BB: 2, CR: 2, SS: 2, DD: 2, AT: 2, TR: 2, FT: 2,
};

/** Is tile (c,r) in `side`'s field of view — a friendly ship within its sight
 *  radius, or an owned city within 2? Tiles outside every range get the shroud. */
export function tileInView(g: GameState, side: Side, c: number, r: number): boolean {
  for (const s of g.ships) {
    if (s.side === side && hexDist(s.c, s.r, c, r) <= (SHIP_SIGHT[s.type] ?? 2)) return true;
  }
  for (const ct of g.cities) {
    if (ct.owner === side && hexDist(ct.c, ct.r, c, r) <= 2) return true;
  }
  return false;
}

export function visibleTo(g: GameState, side: Side, e: Ship): boolean {
  if (e.side === side) return true;
  if (e.fought) return true;
  const mine = g.ships.filter((s) => s.side === side);
  if (mine.some((s) => s.c === e.c && s.r === e.r)) return true;
  if (!(e.type === 'SS' && e.submerged)) {
    if (mine.some((s) => s.type === 'CV' && hexDist(s.c, s.r, e.c, e.r) <= 2)) return true;
  }
  return false;
}

/**
 * What the human sees drawn on the board/minimap — more forgiving than visibleTo
 * so it matches the fog shroud (tileInView): any surface enemy in a tile you can
 * see is shown. Submerged subs still hide except on contact. RENDER-ONLY — the AI
 * must reason through visibleTo, never this.
 */
export function visibleOnBoard(g: GameState, side: Side, e: Ship): boolean {
  if (e.side === side) return true;
  if (e.fought) return true;
  if (g.ships.some((s) => s.side === side && s.c === e.c && s.r === e.r)) return true; // contact
  if (e.type === 'SS' && e.submerged) return false; // subs stay hidden in clear water
  return tileInView(g, side, e.c, e.r);
}

/** Enemy ships `side` can currently see (own ships excluded). */
export function visibleEnemyShips(g: GameState, side: Side): Ship[] {
  return g.ships.filter((s) => s.side !== side && visibleTo(g, side, s));
}

/**
 * Enemy ships the AI is allowed to reason about. `perception` is 0..1: 0 (the
 * fog-honest default) returns only what the side can actually see; 1 returns
 * every enemy (omniscient); in between, the fraction of otherwise-hidden enemies
 * the admiral also senses — a deterministic subset (lowest ids first) so replays
 * are stable. Difficulty scales strength along this axis (harder = sees more),
 * since a bigger fleet self-congests under one-per-tile. Keeping this the single
 * choke-point for enemy perception is what lets us lint ai.ts to fog-honesty.
 */
export function perceivedEnemies(g: GameState, side: Side, perception = 0): Ship[] {
  if (perception <= 0) return visibleEnemyShips(g, side);
  const all = g.ships.filter((s) => s.side !== side);
  if (perception >= 1) return all;
  const visible = visibleEnemyShips(g, side);
  const seen = new Set(visible.map((s) => s.id));
  const hidden = all.filter((s) => !seen.has(s.id)).sort((a, b) => a.id - b.id);
  return [...visible, ...hidden.slice(0, Math.floor(hidden.length * perception))];
}

/**
 * A fog-honest snapshot of G as `side` knows it: enemy ships they cannot see
 * are removed. This is the ONLY view the AI is allowed to read at default
 * difficulty (WP-4 lints ai.ts to import just this).
 *
 * Returns a deep copy — mutating it never affects the real game.
 */
export function fogView(g: GameState, side: Side): GameState {
  const view = JSON.parse(serialize(g)) as GameState;
  view.ships = view.ships.filter((s) => s.side === side || visibleTo(g, side, s));
  return view;
}
